import { pubsub } from '@services';
import { c as createFocusTrap } from './focus-trap.esm-MYAhwz8v.js';

class MenuPanes extends HTMLElement {
  #pane;
  #panes = this.querySelectorAll('[aria-hidden]');
  #unsubscriber;
  #internalFocusTrapStack = [];

  connectedCallback() {
    this.addEventListener('click', (event) => {
      // Handle click on a back button
      const backLink = event.target.closest('[data-back-button]');
      if (backLink) {
        this.#handleBackLinkClick(backLink);
        return;
      }

      // Handle click on a parent link
      const parentLink = event.target.closest('a[data-target-id]');
      if (parentLink) {
        this.#handleParentLinkClick(parentLink, event);
      }
    });

    // When the drawer is closed, reset all panes
    this.#unsubscriber = pubsub.subscribe('drawer:close', ({ id }) => {
      id === 'menu-drawer' && this.#resetPanes();
    });
  }

  disconnectedCallback() {
    this.#unsubscriber();
  }

  /**
   * Handle click on a Parent link
   * @param {HTMLElement} element - The parent link element
   * @param {MouseEvent} event - The click event
   */
  #handleParentLinkClick(element, event) {
    event.preventDefault();
    this.#pane = element.nextElementSibling;
    this.#togglePanes();
  }

  /**
   * Handle click on a Back link
   * @param {HTMLElement} element - The back link element
   */
  #handleBackLinkClick() {
    const lastFocusTrap = this.#internalFocusTrapStack.pop();
    if (lastFocusTrap.active) {
      // The deactivate callback will take care of the rest.
      lastFocusTrap.deactivate();
    }
  }

  /**
   * Open the pane
   */
  #openPane() {
    if (!this.#pane) return;
    this.#pane.ariaHidden = 'false';
    setTimeout(() => {
      const focusTrap = createFocusTrap(this.#pane, {
        onPostDeactivate: () => {
          this.#closePane();
        },
      });
      focusTrap.activate();
      this.#internalFocusTrapStack.push(focusTrap);
    }, 100);
  }

  /**
   * Close the pane
   */
  #closePane() {
    if (!this.#pane) return;

    // Set focus on the parent pane before closing this one
    this.#pane.ariaHidden = 'true';
    const parentPane = this.#pane.parentElement.closest('[aria-hidden]');
    this.#pane = parentPane;
  }

  /**
   * Toggle the panes
   */
  #togglePanes() {
    if (!this.#pane) return;
    this.#pane.ariaHidden === 'true' ? this.#openPane() : this.#closePane();
  }

  /**
   * Reset the panes when drawer closes
   */
  #resetPanes() {
    const timeout = window.DEFAULT_ANIMATION_DURATION || 300;
    setTimeout(() => {
      this.#panes.forEach((pane) => {
        pane.ariaHidden = 'true';
      });
    }, timeout);
  }
}

customElements.define('menu-panes', MenuPanes);
